import React from "react";
import { type Table } from "@tanstack/react-table";

import { TABLE_COLUMN_WIDTHS } from "../constants";

type UsePinnedColumnsProps<TData> = {
  pinnedLeftColumns: string[];
  table: Table<TData>;
};

export function usePinnedColumns<TData>({
  pinnedLeftColumns,
  table,
}: UsePinnedColumnsProps<TData>) {
  const getColumnWidth = React.useCallback(
    (columnId: string, configuredWidth?: number): number => {
      const isSelectionColumn = columnId === "select";
      const defaultWidth = isSelectionColumn
        ? TABLE_COLUMN_WIDTHS.SELECTION
        : TABLE_COLUMN_WIDTHS.DEFAULT;

      return configuredWidth ?? defaultWidth;
    },
    [],
  );
  const calculateLeftOffset = React.useCallback(
    (columnId: string): string => {
      if (!pinnedLeftColumns.includes(columnId)) return "";

      const headers = table.getFlatHeaders();
      const currentIndex = headers.findIndex((h) => h.id === columnId);
      let offset = 0;

      for (let i = 0; i < currentIndex; i++) {
        const header = headers[i];

        if (pinnedLeftColumns.includes(header.id)) {
          const columnWidth = getColumnWidth(
            header.id,
            header.column.columnDef.size,
          );
          offset += columnWidth;
        }
      }

      return `${offset}px`;
    },
    [pinnedLeftColumns, table, getColumnWidth],
  );

  const isLastPinnedColumn = React.useCallback(
    (columnId: string): boolean => {
      if (!pinnedLeftColumns.includes(columnId)) return false;

      const headers = table.getFlatHeaders();

      const existingPinnedColumns = pinnedLeftColumns.filter((pinnedId) =>
        headers.some((header) => header.id === pinnedId),
      );

      return (
        existingPinnedColumns[existingPinnedColumns.length - 1] === columnId
      );
    },
    [pinnedLeftColumns, table],
  );

  const getPinnedColumnProps = React.useCallback(
    (columnId: string) => {
      const isPinned = pinnedLeftColumns.includes(columnId);
      const isLastPinned = isLastPinnedColumn(columnId);
      const leftOffset = calculateLeftOffset(columnId);

      // Find the column to get its configured width
      const headers = table.getFlatHeaders();
      const header = headers.find((h) => h.id === columnId);
      const columnWidth = header
        ? getColumnWidth(columnId, header.column.columnDef.size)
        : 0;
      const columnMaxSize = header ? header.column.columnDef.maxSize : 0;
      const columnMinSize = header ? header.column.columnDef.minSize : 0;

      return {
        isPinned,
        isLastPinned,
        leftOffset,
        columnWidth,
        columnMaxSize,
        columnMinSize,
      };
    },
    [
      pinnedLeftColumns,
      calculateLeftOffset,
      isLastPinnedColumn,
      table,
      getColumnWidth,
    ],
  );

  return {
    calculateLeftOffset,
    isLastPinnedColumn,
    getPinnedColumnProps,
  };
}
